import { useMemo, useState } from "react";
import { SECONDS_MAGIC_LINK_COOLDOWN } from "shared/utils/constants";

import { useHandleMagicLinkVerificationWithArgs } from "../hooks/use-handle-magic-link-verification";
import { ButtonResend } from "~/components/chat/ai/ask/ecommerce/components/auth/resend-otp-button";
import { SvgIcon } from "~/components/icons/svg-icon";
import { InputOTP } from "~/components/input/input-otp";
import { useT } from "~/i18n/use-t";

export const OtpCard: React.FC<{
  email: string;
  mode: "signIn" | "signUp";
}> = ({ email, mode }) => {
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const t = useT();

  const tx = useMemo(
    () => ({
      weveSentAnEmail: t({
        tx: "auth.signInMagic.weveSentAnEmail",
        txData: { email },
      }),
      checkYourEmail: t({ tx: "auth.signInMagic.checkYourEmail" }),
      enterTheCode: t({ tx: "auth.signInMagic.enterTheCode" }),
      confirm: t({ tx: "auth.signInMagic.confirm" }),
      resendCode: t({ tx: "auth.signInMagic.resendCode" }),
    }),
    [email, t],
  );

  const verifyMagicLinkSignIn = useHandleMagicLinkVerificationWithArgs();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoadingConfirm(true);
    const result = await verifyMagicLinkSignIn({
      email,
      code: otp,
      gtmSource: "otp",
    });
    setLoadingConfirm(false);
    if (!result.isSignedIn && result.behavior === "stay") {
      setError(result.message);
      return;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="contents">
      <SvgIcon icon="emailSent" className="max-h-[240px] fill-transparent" />

      <div className="w-full">
        <p className="mb-6 text-center text-2xl font-bold">
          {tx.checkYourEmail}
        </p>
        <p className="text-center text-sm">{tx.weveSentAnEmail}</p>
        {email ? (
          <strong className="mt-0.5 block break-all text-center text-sm font-bold">
            {email}
          </strong>
        ) : null}

        <InputOTP
          value={otp}
          onChange={(value) => {
            setOtp(value);
            setError("");
          }}
          className="mx-auto mb-4 mt-6"
        />

        {error ? (
          <p className="mb-4 text-center text-sm text-error">{error}</p>
        ) : (
          <p className="mb-5 text-center text-sm">{tx.enterTheCode}</p>
        )}
      </div>

      <div className="w-full">
        <button
          title={tx.confirm}
          type="submit"
          className="btn-block btn mb-3 border-0 bg-primary-500 text-[16px] font-normal text-white disabled:bg-primary-500 disabled:text-white disabled:opacity-40"
          disabled={otp.length !== 6 || loadingConfirm}
        >
          {loadingConfirm ? <span className="loading loading-spinner" /> : null}
          {tx.confirm}
        </button>

        <ButtonResend
          mode={mode}
          countdownMs={SECONDS_MAGIC_LINK_COOLDOWN * 1_000}
          className="btn-block btn border-0 bg-primary-50 text-[16px] font-normal text-primary-500 disabled:bg-primary-50 disabled:text-primary-500 disabled:opacity-40"
          data={{ name: "", email }}
        />
      </div>
    </form>
  );
};
